import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/auth-context";
import { useTranslation } from "react-i18next";
import { ROUTE_PATHS } from "@/routing/domainRoutes";
import {
  LayoutDashboard,
  FileText,
  FolderKanban,
  Users,
  CheckCircle2,
  CalendarClock,
  Receipt,
  BarChart3,
  UsersRound,
  Settings,
  ChevronLeft,
} from "lucide-react";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const navItems = [
    { label: t("nav.dashboard"), path: ROUTE_PATHS.dashboardHome, icon: LayoutDashboard },
    { label: t("nav.clients"), path: "/clients", icon: Users },
    { label: t("nav.projects"), path: "/projects", icon: FolderKanban },
    { label: t("nav.tickets"), path: "/tickets", icon: FileText },
    { label: t("nav.schedule"), path: ROUTE_PATHS.schedule, icon: CalendarClock },
    { label: t("nav.payments"), path: "/billing", icon: Receipt },
    { label: t("nav.reports"), path: "/reports", icon: BarChart3 },
    { label: t("nav.team"), path: "/team", icon: UsersRound },
    { label: t("nav.settings"), path: "/settings", icon: Settings },
  ];
  const visibleNavItems = navItems.filter((item) => item.path !== "/team" || user?.currentPlan === "TEAM");

  return (
    <aside
      className={cn(
        "hidden md:flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-200 h-screen sticky top-0 z-30",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-14 px-4 border-b border-sidebar-border">
        <img src="/workscope-mark.svg" alt="WorkScope" className="h-6 w-6 object-contain shrink-0" />
        {!collapsed && (
          <span className="ml-2.5 text-lg font-bold text-sidebar-accent-foreground tracking-tight">
            WorkScope
          </span>
        )}
        <button
          onClick={onToggle}
          className="ml-auto p-1 rounded hover:bg-sidebar-accent transition-colors"
        >
          <ChevronLeft className={cn("h-4 w-4 text-sidebar-muted transition-transform", collapsed && "rotate-180")} />
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        {visibleNavItems.map((item) => {
          const isActive =
            item.path === ROUTE_PATHS.dashboardHome
              ? location.pathname === ROUTE_PATHS.dashboardHome
              : location.pathname === item.path || location.pathname.startsWith(item.path + "/");
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
              )}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
